import { iatan2, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, GargRules, GargUnit, SendaNpc } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The cable's gargoyles (docs/sendas/artifice.md, chapter 2). They come in flocks off the abyss, fast (no running from
 * them on a scaffold or a car's deck), and either sit where the act lets them or go for someone:
 *
 * - perched (`perches`, by kind): on the crane's arm (its weight tilts it), on the car's cable (enough of them stop the
 *   car) or its cabin's roof (they lean down and peck the hero under them), on a slot of the gearbox (nobody can work a
 *   gear with a gargoyle sitting on it). A perched gargoyle stays till it is struck down.
 * - going for the hero or Dunna: straight through the air at `pace`, a blow every `reload` within `reach` (`heroBp` of
 *   the hero's life, `blows` of a person's: the person flinches a moment).
 *
 * Soot crows come with the car's flocks: they never perch, they go for the hero. La Picotera (the gearbox's boss) is in
 * the list too (mode 4): the gearbox moves her, this module only keeps her out of the common rules.
 *
 * The gargoyles are nightmares of the fight (gargoyles and crows in the sim); this module moves them all (`gargTick`
 * from the act's tick; `gargHold` keeps the enemies' tick off them) and strikes their blows itself.
 *
 * Cost: a dozen flyers at most, each looking at its one target once a tick (no nightmare against another).
 */

/** The unit of an enemy (undefined: not one). */
export const gargOf = (s: BattleState, id: number): GargUnit | undefined => {
  const list = s.senda?.garg?.list;
  if (!list) return undefined;
  for (const g of list) if (g.id === id) return g;
  return undefined;
};

/** The gargoyle sitting on (or flying to) a perch (undefined: none). */
const taking = (s: BattleState, perch: number): GargUnit | undefined => s.senda?.garg?.list.find((g) => g.perch === perch && (g.mode === 0 || g.mode === 1));

/** The gargoyle perched on a perch (undefined: none). */
export const gargAt = (s: BattleState, perch: number): GargUnit | undefined => s.senda?.garg?.list.find((g) => g.perch === perch && g.mode === 1);

/** How many sit on perches of a kind (and, of the gearbox's, on a slot: `slot` ≥ 0). */
export const gargOn = (s: BattleState, kind: number, slot = -1): number => {
  const r = s.rules?.senda?.garg;
  const list = s.senda?.garg?.list;
  if (!r || !list) return 0;
  let n = 0;
  for (const g of list) {
    if (g.mode !== 1 || g.perch < 0) continue;
    const p = r.perches[g.perch];
    if (p && p.kind === kind && (slot < 0 || p.slot === slot)) n++;
  }
  return n;
};

/** One flyer off the abyss: a gargoyle for a free perch of `kinds` (`perchBp` of the time), else for the hero or Dunna; a crow for the hero. */
export const gargSpawn = (s: BattleState, crow: boolean, kinds: number[], perchBp: number, k = 0): GargUnit | null => {
  const r = s.rules?.senda?.garg;
  const st = s.senda;
  if (!r || !st) return null;
  const gs = (st.garg ??= { list: [] });
  const x = r.from.x + rngRange(s.rng, -2200, 2200) + k * 500;
  const y = r.from.y + rngRange(s.rng, -1600, 1600) - k * 400;
  const e = spawnEnemy(s, crow ? 'crow' : 'gargoyle', 1, false, 0, x, y, crow ? r.crowHp : r.hp);
  e.anim = 'walk';
  const u: GargUnit = { id: e.id, perch: -1, mode: 2, cd: s.tick + r.reload, crow };
  if (!crow && rngInt(s.rng, 10000) < perchBp) {
    // The free perches of the kinds asked, in their order of liking (the first kind first).
    for (const kind of kinds) {
      const free: number[] = [];
      r.perches.forEach((p, i) => {
        if (p.kind === kind && !taking(s, i)) free.push(i);
      });
      if (!free.length) continue;
      u.perch = free[rngInt(s.rng, free.length)] as number;
      u.mode = 0;
      break;
    }
  }
  if (u.mode === 2) {
    const d = st.npcs.find((n) => n.id === 'dunna' && n.hp > 0 && !n.away);
    if (!crow && d && rngInt(s.rng, 2) === 0) u.mode = 3;
  }
  gs.list.push(u);
  return u;
};

/** A flock: `n` gargoyles and `crows` crows. */
export const gargFlock = (s: BattleState, n: number, crows: number, kinds: number[], perchBp: number): void => {
  for (let k = 0; k < n; k++) gargSpawn(s, false, kinds, perchBp, k);
  for (let k = 0; k < crows; k++) gargSpawn(s, true, kinds, 0, n + k);
};

const strikeHero = (s: BattleState, r: GargRules, e: Enemy, u: GargUnit): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || s.tick < u.cd) return;
  u.cd = s.tick + r.reload;
  e.facing = iatan2(h.y - e.y, h.x - e.x);
  e.anim = 'attack';
  e.animT = 0;
  damageHero(s, h, mulBp(h.stats.maxHp, r.heroBp), e, 'physical', true);
};

const strikeNpc = (s: BattleState, r: GargRules, e: Enemy, u: GargUnit, n: SendaNpc): void => {
  if (s.tick < u.cd) return;
  u.cd = s.tick + r.reload;
  e.facing = iatan2(n.y - e.y, n.x - e.x);
  e.anim = 'attack';
  e.animT = 0;
  n.hp = imax(0, n.hp - r.blows);
  n.flinch = imax(n.flinch, 12);
  if (!n.hit) n.hit = true;
  ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
};

/** The flyers' tick: to their perches, on them, at the hero or Dunna; the struck-down taken off the list. */
export const gargTick = (s: BattleState): void => {
  const r = s.rules?.senda?.garg;
  const st = s.senda?.garg;
  if (!r || !st) return;
  let k = 0;
  for (const u of st.list) {
    const e = s.enemies.find((x) => x.id === u.id);
    if (!e || e.hp <= 0) {
      if (u.mode === 1) ev(s, { t: 'senda', kind: 'unperch', npc: '', x: e?.x ?? 0, y: e?.y ?? 0, n: u.perch });
      continue;
    }
    st.list[k++] = u;
    if (u.mode === 4) continue;
    const h = s.heroes.find((x) => x.downTicks === 0);
    if (u.mode === 0) {
      const p = r.perches[u.perch] as GargRules['perches'][number];
      e.anim = 'walk';
      if (stepToward(e, p.x, p.y, r.pace)) {
        u.mode = 1;
        e.anim = 'idle';
        ev(s, { t: 'senda', kind: 'perch', npc: '', x: p.x, y: p.y, n: u.perch });
      }
      continue;
    }
    if (u.mode === 1) {
      const p = r.perches[u.perch] as GargRules['perches'][number];
      // On a roof it leans down at the hero under it; anywhere else it sits and weighs.
      if (p.kind === 1 && h && withinRange(h.x, h.y, e.x, e.y, r.peckR)) strikeHero(s, r, e, u);
      else if (e.anim === 'attack' && s.tick >= u.cd - r.reload + 6) e.anim = 'idle';
      continue;
    }
    // Going for Dunna (the hero once she is down).
    const d = u.mode === 3 ? s.senda?.npcs.find((n) => n.id === 'dunna' && n.hp > 0 && !n.away) : undefined;
    if (u.mode === 3 && !d) u.mode = 2;
    const tx = d ? d.x : h?.x;
    const ty = d ? d.y : h?.y;
    if (tx === undefined || ty === undefined) continue;
    if (withinRange(e.x, e.y, tx, ty, r.reach)) {
      if (d) strikeNpc(s, r, e, u, d);
      else strikeHero(s, r, e, u);
      continue;
    }
    if (e.anim !== 'attack' || s.tick >= u.cd - r.reload + 6) e.anim = 'walk';
    stepToward(e, tx, ty, r.pace);
  }
  st.list.length = k;
};

/** The flyers are the act's (true: the enemies' tick leaves them be). */
export const gargHold = (s: BattleState, e: Enemy): boolean => !!gargOf(s, e.id);
